import React, { act, useEffect, useRef, useState } from "react";
import Button from "../Button";
import ArrowButton from "../ArrowButton";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, Navigation } from "swiper/modules";
import { filterUltraSpaceBanners, useWindowSize } from "@app/Helpers.js";
import BrandLogo from "../BrandLogo";
import { renderToString } from "react-dom/server";
import PaginationComponent from "../PaginationComponent";
import "./style.scss";
import { useQueries, useQuery } from "@tanstack/react-query";
import QRCode from "qrcode";
import {
  SLIDER_CACHE_TIME,
  SLIDER_STALE_TIME,
  getDashboardSlider,
  getDashboardSliderDescribe,
  markSliderAsSeen,
} from "../../api/slider";
import {
  DETAILS_CACHE_TIME,
  DETAILS_STALE_TIME,
  getUserDetails,
} from "../../api/consumer";
import DOMPurify from "dompurify";

function Countdown({ finishDate, className = "" }) {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  const requestRef = useRef();
  const prevTimeLeftRef = useRef(timeLeft);

  function calculateTimeLeft() {
    const now = new Date().getTime();
    const targetTime = new Date(finishDate).getTime();
    const timeInSeconds = Math.max((targetTime - now) / 1000, 0);

    return {
      days: Math.floor(timeInSeconds / (60 * 60 * 24)),
      hours: Math.floor((timeInSeconds % (60 * 60 * 24)) / (60 * 60)),
      minutes: Math.floor((timeInSeconds % (60 * 60)) / 60),
    };
  }

  function countdown() {
    const newTimeLeft = calculateTimeLeft();

    if (
      newTimeLeft.days !== prevTimeLeftRef.current.days ||
      newTimeLeft.hours !== prevTimeLeftRef.current.hours ||
      newTimeLeft.minutes !== prevTimeLeftRef.current.minutes
    ) {
      setTimeLeft(newTimeLeft);
      prevTimeLeftRef.current = newTimeLeft;
    }

    requestRef.current = setTimeout(() => {
      requestRef.current = requestAnimationFrame(countdown);
    }, 1000);
  }

  useEffect(() => {
    requestRef.current = requestAnimationFrame(countdown);
    return () => clearTimeout(requestRef.current);
  }, [finishDate]);

  const { days, hours, minutes } = timeLeft;

  const paddedDays = String(days).padStart(2, "0");
  const paddedHours = String(hours).padStart(2, "0");
  const paddedMinutes = String(minutes).padStart(2, "0");

  const splitDays = paddedDays.split("");
  const splitHours = paddedHours.split("");
  const splitMinutes = paddedMinutes.split("");

  if (!finishDate) {
    return;
  }

  if (days === 0 && hours === 0 && minutes === 0) {
    return;
  }

  return (
    <div className={`countdown-component ${className}`}>
      <div className="days">
        {splitDays.map((e, index) => (
          <span key={`days_${index}`} className="digit">
            {e}
          </span>
        ))}
        <span className="label">
          Days
          {/* Tage */}
        </span>
      </div>
      <div className="hours">
        {splitHours.map((e, index) => (
          <span key={`hours_${index}`} className="digit">
            {e}
          </span>
        ))}
        <span className="label">
          Hours
          {/* Stunden */}
        </span>
      </div>
      <div className="minutes">
        {splitMinutes.map((e, index) => (
          <span key={`minutes_${index}`} className="digit">
            {e}
          </span>
        ))}
        <span className="label">
          Minutes
          {/* Minuten */}
        </span>
      </div>
    </div>
  );
}

export default function HeroSlider() {
  const { width } = useWindowSize();
  const vHD = width >= 720;

  const urlMobile =
    import.meta.env.VITE_AR_NY_URL +
    "?utm_content=icbuttonnewyear&token=" +
    localStorage.getItem("token");

  const { data: slides, isLoading: isLoadingSlides } = useQuery({
    queryKey: ["homepage-slider"],
    queryFn: getDashboardSlider,
    staleTime: SLIDER_STALE_TIME,
    cacheTime: SLIDER_CACHE_TIME,
  });

  const { data: details, isLoading: isLoadingDetails } = useQuery({
    queryKey: ["user-details"],
    queryFn: getUserDetails,
    staleTime: DETAILS_STALE_TIME,
    cacheTime: DETAILS_CACHE_TIME,
  });

  const ultraInsider = details?.consumer?.ultraInsider ?? false;
  const filteredSliders = filterUltraSpaceBanners(
    slides?.sliders,
    ultraInsider,
  );
  const isSingleSlide = filteredSliders?.length === 1;

  const QRCodeComponent = () => {
    const url =
      import.meta.env.VITE_AR_NY_URL +
      "?utm_content=icqrnewyear&token=" +
      localStorage.getItem("token");

    useEffect(() => {
      const canvas = document.getElementById("qr-code");

      QRCode.toCanvas(canvas, url, { width: 200, height: 200 }, (error) => {
        if (error) console.error(error);
      });
    }, []);

    return (
      <div className="qr-code-outer">
        <canvas id="qr-code" />
      </div>
    );
  };

  // const detailsQueries = useQueries({
  //   queries: (slides?.sliders ?? []).map(slide => ({
  //     queryKey: ['homepage-slider', slide.uuid],
  //     queryFn: () => getDashboardSliderDescribe(slide.uuid),
  //     staleTime: SLIDER_STALE_TIME,
  //     cacheTime: SLIDER_CACHE_TIME,
  //   })),
  // })

  const seenSlidesRef = useRef(new Set());

  const markSlideAsSeenIfNeeded = (uuid) => {
    if (!uuid || seenSlidesRef.current.has(uuid)) return;
    seenSlidesRef.current.add(uuid);
    markSliderAsSeen(uuid);
  };

  const handleSlideView = (swiper) => {
    const slide = filteredSliders?.[swiper.realIndex];
    markSlideAsSeenIfNeeded(slide?.uuid);
  };

  const [activeSkin, setActiveSkin] = useState(() => {
    const stored = localStorage.getItem("activeSkin");
    return stored || null;
  });

  useEffect(() => {
    const updateSkin = () => {
      const stored = localStorage.getItem("activeSkin");
      setActiveSkin(stored || null);
    };

    window.addEventListener("skinChange", updateSkin);
    return () => window.removeEventListener("skinChange", updateSkin);
  }, []);

  return (
    <div
      className={`hero-slider-component full-width ${
        activeSkin ? `skin-${activeSkin}` : ""
      }`}
    >
      <div className="cta-group">
        <div className="swiper-button-prev hero">
          <ArrowButton reverse />
        </div>
        <div className="swiper-button-next hero">
          <ArrowButton />
        </div>
      </div>
      <div className="pagination-main-swiper" />
      {!isLoadingSlides && !isLoadingDetails && (
        <Swiper
          pagination={{
            el: ".pagination-main-swiper",
            type: "custom",
            renderCustom: (_, current, total) => {
              return renderToString(
                <PaginationComponent current={current} total={total} />,
              );
            },
          }}
          loop={true}
          autoplay={{
            delay: 7000,
            disableOnInteraction: false,
          }}
          className={`hero-swiper ${isSingleSlide ? "single" : ""}`}
          spaceBetween={0}
          slidesPerView="auto"
          navigation={{
            nextEl: ".swiper-button-next.hero",
            prevEl: ".swiper-button-prev.hero",
          }}
          modules={[Pagination, Autoplay, Navigation]}
          onSwiper={handleSlideView}
          onSlideChange={handleSlideView}
        >
          {filteredSliders?.map((slide) => (
            <SwiperSlide key={slide.uuid}>
              <div
                className={`slide-wrapper ${slide.ident} ${
                  slide.button?.metadata?.buttonPosition || ""
                }`}
              >
                {slide?.vignette && <div className="overlay"></div>}
                <picture>
                  <source
                    media={` ${
                      isSingleSlide
                        ? "(min-width: 720px)"
                        : "(min-width: 1020px)"
                    }`}
                    srcSet={slide.hero_image?.file?.url}
                  />

                  <img
                    src={slide.thumbnail?.file?.url}
                    alt={slide.thumbnail?.metadata?.imgDesc}
                  />
                </picture>

                <div className="content">
                  {!vHD && slide.ident !== "Neu_ultra_vuse" && (
                    <div className="only-mobile">
                      <BrandLogo brands={slide.brands} />
                    </div>
                  )}
                  {slide.timer && (
                    <Countdown
                      finishDate={slide.timer?.metadata?.date}
                      className="mobile"
                    />
                  )}
                  <div className="copy">
                    <div
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(slide.intro.custom)}}
                      className="inner-copy"
                    ></div>

                    <div className="only-mobile">
                      {slide.ident === "Baner_fireworks" ? (
                        <Button
                          action={() => {
                            dataLayer.push({
                              event: "select_slider",
                              sliders: [{ ...slide }],
                            });
                          }}
                          href={urlMobile}
                          btnClass="yellow"
                        >
                          Feuerwerk starten
                        </Button>
                      ) : slide.button ? (
                        <Button
                          action={() => {
                            dataLayer.push({
                              event: "select_slider",
                              sliders: [{ ...slide }],
                            });
                          }}
                          href={slide.button.custom}
                          btnClass="yellow"
                        >
                          {slide.button?.metadata?.name}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                  {slide.timer && (
                    <Countdown
                      finishDate={slide.timer?.metadata?.date}
                      className="desktop"
                    />
                  )}
                  {vHD && (
                    <div className="brand-footer">
                      {slide.ident !== "Neu_ultra_vuse" && (
                        <BrandLogo brands={slide.brands} />
                      )}
                      {slide.button && (
                        <Button
                          action={() => {
                            dataLayer.push({
                              event: "select_slider",
                              sliders: [{ ...slide }],
                            });
                          }}
                          href={slide.button.custom}
                          btnClass="yellow"
                        >
                          {slide.button?.metadata?.name}
                        </Button>
                      )}
                    </div>
                  )}
                  {vHD && slide.ident === "Baner_fireworks" && (
                    <QRCodeComponent />
                  )}
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </div>
  );
}
